  import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Star, ArrowLeft, MapPin, Navigation } from 'lucide-react'; // Changed MapPin to Navigation for location
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getShopsByCategory, Shop, getCategories } from '../supabase/queries'; // Will create getShopsByCategory and Shop type, added getCategories
import { supabase } from '../supabase/client'; // Import supabase client

// Haversine formula to calculate distance between two points on Earth
const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371; // Radius of Earth in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in kilometers
};

interface ShopCardProps {
  shop: Shop;
  userLatitude: number | null;
  userLongitude: number | null;
}

const ShopCard: React.FC<ShopCardProps> = ({ shop, userLatitude, userLongitude }) => {
  const navigate = useNavigate(); // Initialize useNavigate
  const distance = userLatitude && userLongitude && shop.latitude && shop.longitude
    ? haversineDistance(userLatitude, userLongitude, shop.latitude, shop.longitude).toFixed(2)
    : null;

  return (
    <div
      className="bg-gray-800 p-4 rounded-xl shadow-lg flex items-center mb-4 cursor-pointer hover:bg-gray-700 transition"
      onClick={() => navigate(`/shop/${shop.id}`)} // Navigate to ShopDetailPage on card click
    >
      {/* Column 1: Image/Initials */}
      <div className="flex-shrink-0 mr-4">
        {shop.image_url && (
          <img src={shop.image_url} alt={shop.name} className="w-12 h-12 rounded-full object-cover" />
        )}
        {!shop.image_url && (
          <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
            {shop.name.charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      {/* Column 2: Shop details */}
      <div className="flex-grow">
        <h3 className="text-white text-xl">{shop.name}</h3>
        <p className="text-gray-400 text-sm">{shop.description}</p>
        <div className="flex items-center text-gray-500 text-xs mt-1">
          {shop.average_rating !== null && (
            <>
              <Star className="w-3 h-3 mr-1 text-yellow-400 fill-yellow-400" />
              <span>{shop.average_rating.toFixed(1)}</span>
            </>
          )}
          {distance && (
            <div
              className="flex items-center ml-3 cursor-pointer hover:text-teal-400 transition"
              onClick={(e) => {
                e.stopPropagation(); // Prevent card click from triggering when clicking map icon
                if (shop.latitude && shop.longitude) {
                  window.open(`https://www.google.com/maps/search/?api=1&query=${shop.latitude},${shop.longitude}`, '_blank');
                }
              }}
            >
              <Navigation className="w-3 h-3 mr-1" />
              <span className="underline">{distance} km</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ShopListPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();

  const [shops, setShops] = useState<Shop[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userLatitude, setUserLatitude] = useState<number | null>(null);
  const [userLongitude, setUserLongitude] = useState<number | null>(null);
  const [categoryName, setCategoryName] = useState<string>('Shops'); // State for category name

  // --- User State Management ---
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');

  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session) {
          setIsLoggedIn(true);
          setUserName(session.user?.email || 'User');
        } else {
          setIsLoggedIn(false);
          setUserName('');
          navigate('/login'); // Redirect to login if not authenticated
        }
      }
    );

    // Initial check for session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setIsLoggedIn(true);
        setUserName(session.user?.email || 'User');
      } else {
        navigate('/login'); // Redirect to login if no initial session
      }
    });

    // Get user's current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLatitude(position.coords.latitude);
          setUserLongitude(position.coords.longitude);
        },
        (err) => {
          console.error("Error getting user location:", err);
          setError("Failed to get your location. Shops might not be sorted by distance.");
          // Proceed without location if permission denied or error
          setUserLatitude(null);
          setUserLongitude(null);
        }
      );
    } else {
      setError("Geolocation is not supported by your browser. Shops might not be sorted by distance.");
    }

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);
  // ----------------------------------

  useEffect(() => {
    const fetchCategoryName = async () => {
      if (categoryId) {
        const categories = await getCategories();
        const currentCategory = categories?.find(cat => cat.id === categoryId);
        if (currentCategory) {
          setCategoryName(currentCategory.name);
        } else {
          setCategoryName('Unknown Category');
        }
      }
    };
    fetchCategoryName();
  }, [categoryId]);

  useEffect(() => {
    if (!categoryId) {
      setError('Category ID is missing.');
      setLoading(false);
      return;
    }

    const fetchShops = async () => {
      setLoading(true);
      setError(null);
      try {
        // Only fetch if location is available or if we decide to proceed without it
        // For now, we'll proceed even if location is null, and handle sorting in the query
        const fetchedShops = await getShopsByCategory(categoryId, userLatitude, userLongitude);
        if (fetchedShops) {
          setShops(fetchedShops);
        } else {
          setError('Failed to fetch shops.');
        }
      } catch (e: any) {
        setError(`Error fetching shops: ${e.message}`);
      } finally {
        setLoading(false);
      }
    };

    // Fetch shops whenever categoryId or user location changes
    fetchShops();
  }, [categoryId, userLatitude, userLongitude]);

  const filteredShops = shops.filter(shop =>
    shop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    shop.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleBack = () => {
    navigate(-1); // Go back to the previous page
  };

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error logging out:', error.message);
    } else {
      setIsLoggedIn(false);
      setUserName('');
      console.log('User logged out.');
      navigate('/login'); // Redirect to login after logout
    }
  };

  if (!categoryId) {
    return <div className="text-white text-center">Category ID not found.</div>;
  }

  return (
    <div className="h-screen bg-gray-900 flex flex-col">
      <Header
        isLoggedIn={isLoggedIn}
        userName={userName}
        onLogout={handleLogout}
      />

      <div className="flex-grow p-6 pt-0 overflow-y-auto">
        <div className="flex items-center mb-6">
          <button onClick={handleBack} className="p-3 rounded-full hover:bg-teal-700 transition mr-2">
            <ArrowLeft className="w-7 h-7 text-white" />
          </button>
          <h2 className="text-3xl  text-white ml-4">List of {categoryName}</h2>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search shops..."
            className="w-full py-3 pl-10 pr-4 bg-gray-800 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-600"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {loading && <p className="text-white text-center">Loading shops...</p>}
        {error && <p className="text-red-500 text-center">{error}</p>}
        {!loading && filteredShops.length === 0 && !error && (
          <p className="text-gray-400 text-center">No shops found for this category.</p>
        )}

        <div className="space-y-4">
          {filteredShops.map(shop => (
            <ShopCard key={shop.id} shop={shop} userLatitude={userLatitude} userLongitude={userLongitude} />
          ))}
        </div>
      </div>

    </div>
  );
};

export default ShopListPage;
